import { useDeferredValue, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import { useCart } from '../context/CartContext';
import { getProducts } from '../lib/api';
import { formatNpr } from '../lib/currency';
import { getOptimizedImageUrl } from '../lib/imageUrl';
import { getProductDisplayId } from '../lib/productDisplayId';

function ProductsPage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingProductId, setAddingProductId] = useState('');
  const [searchValue, setSearchValue] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortValue, setSortValue] = useState('newest');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const { addItem } = useCart();
  const deferredSearchValue = useDeferredValue(searchValue);

  useEffect(() => {
    async function fetchProducts() {
      try {
        setProducts(await getProducts());
      } catch (fetchError) {
        setError(fetchError.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchProducts();
  }, []);

  useEffect(() => {
    setSearchValue(searchParams.get('q') || '');
  }, [searchParams]);

  async function handleAddToCart(productId) {
    setAddingProductId(productId);

    try {
      await addItem(productId, 1);
    } catch (_error) {
      // CartContext surfaces the real error message.
    } finally {
      window.setTimeout(() => {
        setAddingProductId('');
      }, 700);
    }
  }

  const normalizedQuery = deferredSearchValue.trim().toLowerCase();
  const categories = ['All', ...new Set(products.map((product) => product.category))];
  const exactMatches = [];
  const partialMatches = [];

  for (const product of products) {
    const normalizedName = product.name.toLowerCase();
    const normalizedCategory = product.category.toLowerCase();
    const matchesCategory =
      selectedCategory === 'All' || product.category === selectedCategory;

    if (!matchesCategory) {
      continue;
    }

    if (!normalizedQuery) {
      partialMatches.push(product);
      continue;
    }

    if (normalizedName === normalizedQuery) {
      exactMatches.push(product);
      continue;
    }

    if (
      normalizedName.includes(normalizedQuery) ||
      normalizedCategory.includes(normalizedQuery)
    ) {
      partialMatches.push(product);
    }
  }

  const filteredProducts = normalizedQuery
    ? [...exactMatches, ...partialMatches]
    : partialMatches;
  const sortedProducts = [...filteredProducts].sort((left, right) => {
    if (sortValue === 'price-low') {
      return Number(left.price) - Number(right.price);
    }

    if (sortValue === 'price-high') {
      return Number(right.price) - Number(left.price);
    }

    return 0;
  });

  return (
    <section className="page-card products-page">
      <div className="products-page__hero">
        <p className="products-page__crumb">Home / Shop / Products</p>
        <h1 className="page-card__title products-page__title">All Products</h1>
        <p className="page-card__text products-page__text">
          Browse our full collection of frames, sunglasses, and lenses —
          filter by category or search to find the perfect pair.
        </p>
      </div>

      <section className="products-offers">
        <div className="products-offers__simple">
          <div>
            <p className="products-offers__eyebrow">Today Offer</p>
            <h2>Get today offer products with up to 50% discount.</h2>
          </div>
          <Link className="green-button products-offers__button" to="/offers">
            View Offer
          </Link>
        </div>
      </section>

      <div className="products-layout">
        <aside
          className={`products-sidebar ${isMobileFiltersOpen ? 'is-mobile-open' : ''}`}
        >
          <div className="products-sidebar__sheet-handle" />
          <div className="products-sidebar__sheet-header">
            <h2 className="products-sidebar__sheet-title">Filters</h2>
            <button
              type="button"
              className="products-sidebar__sheet-close"
              onClick={() => {
                setIsMobileFiltersOpen(false);
              }}
            >
              ×
            </button>
          </div>

          <div className="products-sidebar__section">
            <p className="products-sidebar__label">Filter By Category</p>
            <div className="products-filters products-filters--stacked">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`products-filter ${selectedCategory === category ? 'is-active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsMobileFiltersOpen(false);
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="products-sidebar__section">
            <p className="products-sidebar__label">Shop Notes</p>
            <div className="products-sidebar__meta">
              <span>{products.length} products listed</span>
              <span>Secure cart flow</span>
              <span>COD checkout available</span>
            </div>
          </div>
        </aside>

        <div className="products-main">
          <div className="products-toolbar">
            <div className="products-toolbar__left">
              <button
                type="button"
                className="products-filter-toggle"
                onClick={() => {
                  setIsMobileFiltersOpen((current) => !current);
                }}
              >
                Filters
              </button>
              <div className="products-search__meta">
                <span>{sortedProducts.length} products available</span>
              </div>
            </div>
            <label className="products-sort">
              <span>Sort By</span>
              <select
                value={sortValue}
                onChange={(event) => {
                  setSortValue(event.target.value);
                }}
              >
                <option value="newest">Newest</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </label>
          </div>

          {isLoading && <p className="products-state">Loading products...</p>}
          {error && <p className="products-state products-state--error">{error}</p>}
          {!isLoading && !error && products.length === 0 && (
            <p className="products-state">No products found.</p>
          )}
          {!isLoading && !error && products.length > 0 && sortedProducts.length === 0 ? (
            <p className="products-state">No exact product found for "{searchValue}".</p>
          ) : null}

          <div className="products-grid products-grid--catalog">
            {sortedProducts.map((product) => (
              <article key={product.id} className="product-card product-card--catalog">
                <Link className="product-card__media-link" to={`/products/${product.id}`}>
                  <img
                    className="product-card__image"
                    src={getOptimizedImageUrl(product.image, 600)}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                  />
                </Link>
                <div className="product-card__body">
                  <p className="product-card__id">Product ID: {getProductDisplayId(product.id)}</p>
                  <h2 className="product-card__name">
                    <Link to={`/products/${product.id}`}>{product.name}</Link>
                  </h2>
                  <div className="product-card__pricing">
                    <span className="product-card__price-old">
                      {formatNpr(Number(product.price) * 1.2)}
                    </span>
                    <span className="product-card__price">
                      {formatNpr(product.price)}
                    </span>
                  </div>
                  <div className="product-card__actions">
                    <GreenButton
                      text={addingProductId === product.id ? 'Added' : 'Add to Cart'}
                      onClick={() => {
                        void handleAddToCart(product.id);
                      }}
                    />
                    <Link className="product-card__link" to={`/products/${product.id}`}>
                      View
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductsPage;
